import React from 'react'

const MAX_COMMENT_LENGTH = 1000

interface RetractFormProps {
  /** Whether the "My Approver asked" checkbox is checked. */
  partialRetract: boolean
  onPartialRetractChange: (checked: boolean) => void
  /** Current comments textarea value. */
  comments: string
  onCommentsChange: (value: string) => void
  /** API-level error message to display, if any. */
  retractError: string | null
  /** True while the submit API call is in flight. */
  isSubmitting: boolean
  /** Called when the "Yes, Retract Request" button is clicked. */
  onSubmit: () => void
  /** Called when the "Cancel" button is clicked. */
  onCancel: () => void
}

/**
 * Shared inline retract-request form used by ApprovedView, OverriddenView,
 * and PartiallyApprovedTimeOffView. Renders the instructional paragraph,
 * the "My Approver asked" checkbox, the comments textarea with character
 * counter, an optional API error, and the Submit / Cancel action buttons.
 */
const RetractForm: React.FC<RetractFormProps> = ({
  partialRetract,
  onPartialRetractChange,
  comments,
  onCommentsChange,
  retractError,
  isSubmitting,
  onSubmit,
  onCancel,
}) => (
  <>
    <div className="mb-4">
      <p className="p3 -medium">
        You are requesting to retract this Approved Time Off Request. This request is pending until
        action by the Authorized Approver. To send this request to your Authorized Approver, click
        the <b>Yes, Retract Request</b> button.
      </p>
    </div>

    <div className="mb-4 mt-6!">
      <div className="mb-4 flex items-center">
        <input
          id="partial-retract-cb"
          type="checkbox"
          checked={partialRetract}
          onChange={(e) => onPartialRetractChange(e.target.checked)}
          className="mr-2 cursor-pointer"
        />
        <label htmlFor="partial-retract-cb" className="p3 -book cursor-pointer">
          My Approver asked that I retract this Time Off Request
        </label>
      </div>

      <div className="mt-6!">
        <label htmlFor="retract-comments" className="block p3 -medium mb-1">
          Comments
        </label>
        <textarea
          id="retract-comments"
          className="w-full border rounded p-2 min-h-20"
          rows={4}
          maxLength={MAX_COMMENT_LENGTH}
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
        />
      </div>
      <div className="text-left text-xs text-gray-500 mt-1">
        {MAX_COMMENT_LENGTH - comments.length} of {MAX_COMMENT_LENGTH} characters left
      </div>
    </div>

    {retractError && <p className="text-red-700 mb-3">{retractError}</p>}

    <div className="flex gap-3 mt-8!">
      <button
        className="button -primary"
        onClick={onSubmit}
        disabled={isSubmitting}
        style={isSubmitting ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
      >
        {isSubmitting ? 'Submitting...' : 'Yes, Retract Request'}
      </button>
      <button
        className="button"
        onClick={onCancel}
        disabled={isSubmitting}
      >
        Cancel
      </button>
    </div>
  </>
)

export default RetractForm
